import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { listContacts } from '../api'
import { listOrganisations } from '../organisations/api'

export interface RelationPick {
  type: 'contact' | 'organisatie'
  id: string
  name: string
  detail?: string | null
}

const SEARCH_DELAY_MS = 250

/** A search box over contacts and organisations; the answer to the text in the box is listed below it. */
export function RelationSearch({
  label,
  onPick,
}: {
  label: string
  onPick: (pick: RelationPick) => void
}) {
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<{ text: string; items: RelationPick[] | null } | null>(null)
  const lookup = useRef(0)
  const text = query.trim()

  useEffect(() => {
    if (!text) return
    const mine = ++lookup.current
    const timer = window.setTimeout(() => {
      Promise.all([listContacts({ q: text, limit: 6 }), listOrganisations({ q: text, page_size: 6 })])
        .then(([contacts, organisations]) => {
          if (mine !== lookup.current) return
          setAnswer({
            text,
            items: [
              ...contacts.items.map((item) => ({
                type: 'contact' as const,
                id: item.person_id,
                name: item.full_name,
                detail: item.primary_organisation?.name ?? item.organisation,
              })),
              ...organisations.items.map((item) => ({
                type: 'organisatie' as const,
                id: item.id,
                name: item.name,
                detail: 'Organisatie',
              })),
            ],
          })
        })
        .catch(() => {
          if (mine === lookup.current) setAnswer({ text, items: null })
        })
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [text])

  const current = answer && answer.text === text ? answer : null
  return (
    <div className="space-y-2">
      <Input
        aria-label={label}
        placeholder="Zoek een contact of organisatie"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {current?.items === null ? (
        <p role="alert" className="text-sm text-destructive">
          Zoeken is niet gelukt.
        </p>
      ) : null}
      {current?.items && current.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Niets gevonden.</p>
      ) : null}
      <ul className="space-y-1">
        {(current?.items ?? []).map((item) => (
          <li key={`${item.type}:${item.id}`}>
            <button
              type="button"
              className="flex min-h-11 w-full flex-col items-start justify-center rounded-lg border border-border px-3 text-left hover:bg-muted"
              onClick={() => onPick(item)}
            >
              <span className="text-base font-medium">{item.name}</span>
              {item.detail ? <span className="text-sm text-muted-foreground">{item.detail}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
